/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TechnologySourcing.module.scss';

type Translate = (key: string) => string;

type Item = {
  key: string;
  title: string;
  lead: string;
  tags?: string[];
};

const getItems = (t: Translate): Item[] => [
  {
    key: 'source',
    title: t('Read straight from the register'),
    lead: t('400+ government registers across 200+ countries, read at the source by pipelines we run ourselves.'),
  },
  {
    key: 'source_url',
    title: t('Every field names its register, and links to it'),
    lead: t('Which authority each value came from, its filing reference, and the URL of the page it sits on.'),
  },
  {
    key: 'timestamps',
    title: t('Two dates on everything'),
    lead: t('When the company filed it, and when we last read it.'),
  },
  {
    key: 'document',
    title: t('The original filing comes with it'),
    lead: t('The document reference the value was taken from, carried on the record.'),
  },
  {
    key: 'rights',
    title: t('Yours to sell, redistribute and train on'),
    lead: t('Reseller and derivative rights are available, priced separately from access.'),
    tags: [t('resell'), t('redistribute'), t('train')],
  },
];

export const TechnologySourcing = () => {
  const { t } = useInt();
  const items = getItems(t);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = itemRefs.current.indexOf(entry.target as HTMLDivElement);
            if (index !== -1) {
              setActive(index);
            }
          }
        });
      },
      { rootMargin: '-50% 0px -50% 0px', threshold: 0 },
    );

    itemRefs.current.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.side}>
          <TitleBlock
            align="left"
            extra={t('why us — sourcing and rights')}
            title={t('Five things that decide whether you can build on it.')}
          />
          <div className={styles.count}>
            <span className={styles.cur}>{String(active + 1).padStart(2, '0')}</span>
            <span className={styles.sl}>/</span>
            <span className={styles.tot}>{String(items.length).padStart(2, '0')}</span>
          </div>
        </div>

        <div className={styles.list}>
          {items.map((item, i) => (
            <div
              className={cn(styles.item, active === i && styles.itemOn)}
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              key={item.key}
            >
              <span className={styles.k}>{item.key}</span>
              <div>
                <h3 className={styles.itemTitle}>{item.title}</h3>
                <p className={styles.lead}>{item.lead}</p>
                {item.tags && (
                  <div className={styles.tags}>
                    {item.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
